import type { ColumnDef } from '@tanstack/react-table';

import { DataTableColumnHeader } from '@/components/dataTable/ColumnHeader';
import { Can } from '@/components/can/Can';
import { PERMS } from '@/features/auth/permissions/permissionKeys';
import ManageSlabsContainer from '@/features/points-management/point-setup-forms/ManageSlabsContainer';
import { DeleteIcon, EditIcon } from '@/icons';
import { SlabsData } from '@/services/pointConfiguration/pointConfiguration.types';
import { PointAgentTab } from '../../../pointConfiguration.constants';
import { Button } from 'ui/button';
import { ReactNode } from 'react';
import { useDynamicLocale } from '@/hoslog-intl';

type OnOpen = (config: {
  title: string;
  modalTitle: string | null;
  okText: string | ReactNode;
  component: ReactNode;
  cancelText?: string | ReactNode;
  size?: 'sm' | 'md' | 'lg';
}) => void;

type OpenConfirmation = (config: {
  title: string;
  description: string;
  cancelLabel: string;
  actionLabel: string;
  size?: 'sm' | 'md' | 'lg';
  variant?: 'default' | 'destructive' | 'warning';
  onAction: () => Promise<void>;
  onCancel: () => void;
}) => void;

type SlabColumnOptions = {
  onOpen: OnOpen;
  openConfirmation: OpenConfirmation;
  deleteSlab: (pointSlabId: string) => Promise<unknown>;
  pointCategoryId: string;
  categoryName?: string;
  activeTab: PointAgentTab;
  t: ReturnType<typeof useDynamicLocale>['t'];
};

export function getColumns({
  onOpen,
  openConfirmation,
  deleteSlab,
  pointCategoryId,
  categoryName,
  activeTab,
  t,
}: SlabColumnOptions): ColumnDef<SlabsData>[] {
  return [
    {
      id: 'sn',
      header: ({ column }) => <DataTableColumnHeader column={column} title={t('common.page.tableColumn.sn')} />,
      cell: ({ row }) => <div className="max-w-[10rem] truncate text-caption font-medium">{row.index + 1}</div>,
      enableSorting: false,
    },
    {
      accessorKey: 'fromAmount',
      header: ({ column }) => (
        <DataTableColumnHeader column={column} title={t('pointManagement.page.form.manageSlabs.fromAmount')} />
      ),
      cell: ({ row }) => (
        <div className="max-w-[20rem] truncate text-caption font-medium">{row.getValue('fromAmount')}</div>
      ),
      meta: {
        label: t('pointManagement.page.form.manageSlabs.fromAmount'),
        align: 'right',
      },
      enableHiding: false,
    },
    {
      accessorKey: 'toAmount',
      header: ({ column }) => (
        <DataTableColumnHeader column={column} title={t('pointManagement.page.form.manageSlabs.toAmount')} />
      ),
      cell: ({ row }) => <div className="text-caption font-medium">{row.getValue('toAmount')}</div>,
      meta: {
        label: t('pointManagement.page.form.manageSlabs.toAmount'),
        align: 'right',
      },
      enableHiding: false,
    },
    {
      accessorKey: 'value',
      header: ({ column }) => (
        <DataTableColumnHeader column={column} title={t('pointManagement.page.form.manageSlabs.value')} />
      ),
      cell: ({ row }) => <div className="text-caption font-medium">{row.getValue('value')}</div>,
      meta: {
        label: t('pointManagement.page.form.manageSlabs.value'),
        align: 'right',
      },
      enableHiding: false,
    },
    {
      id: 'actions',
      header: ({ column }) => <DataTableColumnHeader column={column} title={t('common.page.tableColumn.action')} />,
      enableSorting: false,
      enableHiding: false,
      cell: ({ row }) => (
        <div className="flex items-center gap-4 text-caption font-medium">
          <Can rule={{ allOf: [PERMS.point.management.slab.update, PERMS.point.management.slab.details] }}>
            <EditIcon
              className="size-4 cursor-pointer text-foreground-subtle"
              onClick={() =>
                onOpen({
                  modalTitle: 'pointManagement.page.form.manageSlabs.title',
                  title: t('pointManagement.page.form.managePointSlab.title'),
                  okText: t('common.page.button.submit'),
                  cancelText: t('common.page.button.cancel'),
                  size: 'lg',
                  component: (
                    <ManageSlabsContainer
                      pointCategoryId={pointCategoryId}
                      categoryName={categoryName}
                      activeTab={activeTab}
                      pointSlabId={row.original.pointSlabId}
                    />
                  ),
                })
              }
            />
          </Can>
          <Can rule={{ allOf: [PERMS.point.management.slab.delete] }}>
            <Button
              variant="ghost"
              className="p-0"
              onClick={() =>
                openConfirmation({
                  title: t('common.page.modal.confirm'),
                  description: t('common.page.modal.deleteItemMessage'),
                  cancelLabel: t('common.page.modal.no'),
                  actionLabel: t('common.page.modal.submit'),
                  size: 'lg',
                  variant: 'destructive',
                  onAction: async () => {
                    await deleteSlab(row.original.pointSlabId);
                  },
                  onCancel: () => {},
                })
              }
            >
              <DeleteIcon className="size-4 text-destructive-text" />
            </Button>
          </Can>
        </div>
      ),
    },
  ];
}
